import { Link } from 'react-router-dom';
import MarketingLayout from './marketing/MarketingLayout';
import { Article, ArticleHeader, Section, Callout } from './marketing/Article';

const EFFECTIVE_DATE = 'September 2, 2026';

function TermsOfService() {
  return (
    <MarketingLayout>
      <Article>
        <ArticleHeader title="Terms of Service" subtitle={`Last updated: ${EFFECTIVE_DATE}`} />

        <Callout title="At a glance" className="mt-10 mb-12">
          <p>
            Steadily.me is a habit-tracking app provided by Reality Moss LLC. Be reasonable, don't abuse the service, and you can
            leave at any time and take your data with you. Steadily.me Pro is billed through the app stores or RevenueCat's
            checkout and you can cancel anytime. The full terms are below.
          </p>
        </Callout>

        <div className="space-y-10 text-[var(--color-ink-main)] leading-relaxed">
          <Section title="1. Acceptance of these terms">
            <p>
              These Terms of Service ("Terms") are an agreement between you and Reality Moss LLC ("we," "us," "Reality Moss")
              governing your use of the Steadily.me apps and the website at steadily.me (together, the "Service").
              By creating an account or using the Service, you agree to these Terms. If you don't agree, please don't use the Service.
            </p>
          </Section>

          <Section title="2. The service">
            <p>
              Steadily.me is a habit-tracking app provided by Reality Moss LLC, an Ohio limited liability company. The Service is
              available as a web app at steadily.me and as downloadable apps for iOS, Android, macOS, Windows, and Linux. It is
              designed to be left running on a screen you see every day, so it includes display features such as keeping the
              screen awake, dimming or switching the display off on a schedule, and starting automatically at login where you
              turn those on. The core Service is free; some features are part of Steadily.me Pro, a paid upgrade described in
              Section 6. We may add, change, or remove features over time.
            </p>
          </Section>

          <Section title="3. Your account">
            <p>
              You need an account to use the Service, and you must confirm your email address before you can sign in. You can
              sign up with an email address and password or with Sign in with Google or Sign in with Apple. You're responsible
              for keeping your login credentials safe and for everything that happens on your account. Once signed in, a
              device stays signed in until you log out, sign out of all devices, change your password, or delete your account.
              Please notify us at <a href="mailto:support@steadily.me" className="underline">support@steadily.me</a> if you
              believe your account has been compromised.
            </p>
            <p>
              Free accounts sync on one device at a time. If you sign in on a second device, that device shows a local copy
              and offers a "Use this device instead" button that moves your sync slot to it; the previous device then stops
              syncing until you move the slot back.
              Steadily.me Pro removes this limit.
            </p>
            <p>You must be at least 13 years old to use Steadily.me.</p>
          </Section>

          <Section title="4. Acceptable use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service to harass, harm, or impersonate others.</li>
              <li>Attempt to access another user's account, data, or content.</li>
              <li>Probe, scan, or test the security of the Service without our prior written permission.</li>
              <li>Use bots, scrapers, or automated tools to access the Service other than through the apps and the data export described in Section 5.</li>
              <li>Interfere with the operation of the Service or place an unreasonable load on our infrastructure.</li>
              <li>Circumvent the device limit, Pro features, purchase verification, or the rewarded-ad reward.</li>
              <li>Use the Service to violate any law, or to send spam, malware, or other harmful content, including through the Support screen.</li>
            </ul>
          </Section>

          <Section title="5. Your content">
            <p>
              The habits, completions, rest weeks, and support messages you create in Steadily.me are yours. You own them. By
              using the Service, you grant us a limited, worldwide, royalty-free license to store, copy, transmit, and display
              your content solely for the purpose of operating the Service for you, including syncing it between your devices
              and answering your support requests. This license ends when you delete your content or your account, except to
              the extent we are legally required to retain copies for a limited period.
            </p>
            <p>
              You are responsible for the content you create and for making sure you have the right to store it on the Service.
            </p>
            <p>
              <strong>Data export will always be free</strong>, regardless of any paid tier or other monetization change. You can
              download a complete copy of your habits, completions, and rest weeks at any time from Settings, then Account,
              then Export my data (JSON). If you stop using Steadily.me, or stop paying for Pro, you will continue to be able
              to export your data.
            </p>
          </Section>

          <Section title="6. Steadily.me Pro, billing, and promotions">
            <p>
              The core of Steadily.me is free. <strong>Steadily.me Pro</strong> is an optional paid upgrade, available as an
              auto-renewing monthly or annual subscription or as a one-time lifetime purchase. Pro currently includes syncing
              across more than one device, browsing past years, exporting your year as a picture, and additional watercolor
              backgrounds and colors. We may change what Pro includes over time, but we will not remove your data or your
              ability to export it if Pro lapses.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Where you're billed.</strong> On iOS the Apple App Store processes your purchase; on Android, Google
                Play does. On the web and on desktop, and on Android devices without Google Play, the app sends you to a
                checkout page run by RevenueCat where Stripe processes the payment. Purchases on every platform are managed
                through RevenueCat and unlock Pro on your Steadily.me account, so Pro follows your account across devices.
                Steadily.me never receives or stores your payment details.
              </li>
              <li>
                <strong>Auto-renewal.</strong> Subscriptions renew automatically at the end of each billing period unless you
                cancel at least 24 hours before the period ends. Renewal is charged to the payment method on file with the
                store or checkout you purchased through.
              </li>
              <li>
                <strong>Free trials.</strong> Subscriptions may include a free trial (currently one month where offered). You
                can cancel before the trial ends and you won't be charged. Any unused trial time is forfeited when you convert
                to a paid subscription.
              </li>
              <li>
                <strong>Canceling.</strong> You can cancel at any time through the subscription settings of the store you
                purchased from (App Store or Google Play), or, for web checkout purchases, through the "Manage subscription"
                link on the Pro screen. You keep Pro access until the end of the period you've already paid for.
              </li>
              <li>
                <strong>Deleting your account does not cancel a subscription.</strong> Cancel it with the store or checkout you
                bought it from before or after deleting your account, or you may continue to be billed.
              </li>
              <li>
                <strong>Refunds.</strong> Refunds are handled by the store that processed your purchase, under its own
                policies. For App Store and Google Play purchases, please request refunds directly from Apple or Google.
                If you purchased through the web checkout, contact us at
                <a href="mailto:support@steadily.me" className="underline"> support@steadily.me</a>.
              </li>
              <li>
                <strong>Price changes.</strong> If we change subscription prices, the new price applies only from your next
                renewal, and the store will notify you as its rules require.
              </li>
              <li>
                <strong>Free weeks for watching an ad.</strong> On the iOS and Android apps you may be offered a free week of
                Pro in exchange for watching a rewarded video ad. The week is credited only after the ad network confirms you
                watched the full ad; weeks you earn are added to any Pro time you already have. This offer is promotional: it
                may not be available in every country or on every device, and we may change, limit, or discontinue it at any
                time. Any week you have already earned runs for its full duration.
              </li>
              <li>
                <strong>Other promotional offers.</strong> We may from time to time offer free or discounted access to Pro
                features through trials, promo codes, or offer codes. These are promotional and may be changed or withdrawn,
                but any access you have already been granted runs for its full stated duration.
              </li>
            </ul>
          </Section>

          <Section title="7. Automatic updates">
            <p>
              The iOS app is updated only through the App Store, and the web app updates whenever you reload it. The desktop
              and Android apps also receive app-store updates where applicable, and in addition check our update server
              periodically and, by default, download and install new versions of their content automatically during the
              early morning hours, restarting the app to apply them. You can turn automatic installation off, or choose the beta channel, in Settings. Beta versions are
              provided for early feedback and may be less stable than stable releases.
            </p>
          </Section>

          <Section title="8. Termination">
            <p>
              You can delete your account at any time from Settings, then Account, then Delete account, or at{' '}
              <Link to="/delete-account" className="underline">steadily.me/delete-account</Link>. You can also stop using the
              Service at any time without notice.
            </p>
            <p>
              We may suspend or terminate your access to the Service if you materially violate these Terms, if your use creates
              a risk or legal exposure for us, or if we are required to do so by law. Where reasonable, we will give you notice
              and a chance to fix the problem first.
            </p>
            <p>
              On termination, we will delete your account and content as described in our
              <Link to="/privacy" className="underline"> Privacy Policy</Link>, except where we are legally required to retain it.
            </p>
          </Section>

          <Section title="9. Privacy">
            <p>
              Our <Link to="/privacy" className="underline">Privacy Policy</Link> explains what information we collect and how we
              use it. By using the Service, you agree to the practices described in the Privacy Policy.
            </p>
          </Section>

          <Section title="10. Support">
            <p>
              You can reach us through the Support screen in the app or on the website, or by email at{' '}
              <a href="mailto:support@steadily.me" className="underline">support@steadily.me</a>. Support is provided by
              people on a reasonable-efforts basis; we do not guarantee response times.
            </p>
          </Section>

          <Section title="11. Third-party services">
            <p>
              Steadily.me lets you sign in with Google or Apple; processes purchases through the Apple App Store, Google Play,
              RevenueCat, and Stripe; serves optional rewarded ads through Google AdMob; is delivered through Cloudflare and
              hosted on Railway; sends email through Resend; and is distributed through the Apple App Store, Google Play, and
              the Samsung Galaxy Store. Your use of those third-party services is governed by their own terms and privacy
              policies. We are not responsible for the practices of third-party services.
            </p>
          </Section>

          <Section title="12. Disclaimer of warranties">
            <p>
              The Service is provided "as is" and "as available," without warranties of any kind, whether express or implied,
              including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.
              We do not warrant that the Service will be uninterrupted, error-free, or perfectly accurate. Steadily.me is a habit
              tracker, not a medical, mental-health, or fitness service, and nothing in the app is medical advice, including
              the rest-week feature.
            </p>
            <p>
              Steadily.me is designed to run for long periods on an always-on screen. You are responsible for deciding whether a
              device is suitable for that use, for its power and brightness settings, and for any screen wear, image
              retention, or energy cost that results.
            </p>
          </Section>

          <Section title="13. Limitation of liability">
            <p>
              To the maximum extent permitted by law, Reality Moss LLC and its members, employees, and contractors will not be
              liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for any loss of
              profits, revenues, data, or goodwill, arising out of or relating to your use of the Service.
            </p>
            <p>
              Our total liability to you for any claim arising out of or relating to these Terms or the Service is limited to the
              greater of (a) the amount you have paid us for the Service in the twelve months before the event giving rise to the
              claim, or (b) one hundred U.S. dollars (USD $100).
            </p>
          </Section>

          <Section title="14. Indemnification">
            <p>
              You agree to defend and indemnify Reality Moss LLC against any claims, damages, and expenses (including reasonable
              attorneys' fees) arising out of your misuse of the Service or your violation of these Terms or applicable law.
            </p>
          </Section>

          <Section title="15. Changes to these terms">
            <p>
              We may update these Terms from time to time. If we make material changes, we will notify you through the app or by
              email at least 14 days before the changes take effect. Continued use of the Service after the changes take effect
              means you accept the updated Terms. If you don't agree to the new Terms, you can delete your account.
            </p>
          </Section>

          <Section title="16. Governing law and disputes">
            <p>
              These Terms are governed by the laws of the State of Ohio, without regard to its conflict-of-laws rules. You and
              Reality Moss agree to the exclusive jurisdiction of the state and federal courts located in Ohio for any disputes
              arising out of or relating to these Terms or the Service, except that either party may seek injunctive relief in
              any court with jurisdiction.
            </p>
          </Section>

          <Section title="17. Miscellaneous">
            <p>
              These Terms, together with our <Link to="/privacy" className="underline">Privacy Policy</Link>, are the entire
              agreement between you and us regarding the Service. If a provision of these Terms is found unenforceable, the rest
              remains in effect. Our failure to enforce a provision is not a waiver of the right to enforce it later. You may
              not assign these Terms without our consent; we may assign them in connection with a merger, acquisition, or sale
              of assets.
            </p>
            <p>
              If you downloaded the app from the Apple App Store, Apple's standard{' '}
              <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" className="underline" target="_blank" rel="noopener noreferrer">
                Licensed Application End User License Agreement
              </a>{' '}
              also governs your license to that app. If it conflicts with these Terms about the app license itself, that
              agreement controls.
            </p>
          </Section>

          <Section title="18. Contact">
            <p>
              Questions about these Terms? Email
              <a href="mailto:support@steadily.me" className="underline"> support@steadily.me</a>.
            </p>
            <p className="text-sm text-[var(--color-ink-light)] mt-4">
              Reality Moss LLC<br />
              An Ohio limited liability company
            </p>
          </Section>
        </div>
      </Article>
    </MarketingLayout>
  );
}

export default TermsOfService;
